import {Controller} from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "checkbox",
    "email",
    "marketingOptIn",
    "shippingName",
    "shippingCompany",
    "shippingLine1",
    "shippingLine2",
    "shippingPostcode",
    "shippingCity",
    "phoneCountry",
    "phoneNumber",
  ];

  static values = {
    storeName: String,
  };

  get storageKey() {
    return `checkout_information_${this.storeNameValue}`;
  }

  connect() {
    this.loadSavedData();
    this.addFieldListeners();
  }

  toggle() {
    if (this.checkboxTarget.checked) {
      this.saveFormData();
    } else {
      this.removeSavedData();
    }
  }

  saveFormData() {
    const formData = this.collectFormData();
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(formData));
    } catch (error) {
      console.error("Failed to save form data to localStorage:", error);
    }
  }

  removeSavedData() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.error("Failed to remove form data from localStorage:", error);
    }
  }

  collectFormData() {
    const data = {};

    // Contact information
    if (this.hasEmailTarget) {
      data.email = this.emailTarget.value;
    }
    if (this.hasMarketingOptInTarget) {
      data.marketing_opt_in = this.marketingOptInTarget.checked;
    }

    // Shipping address
    if (this.hasShippingNameTarget) {
      data.shipping_name = this.shippingNameTarget.value;
    }
    if (this.hasShippingCompanyTarget) {
      data.shipping_company = this.shippingCompanyTarget.value;
    }
    if (this.hasShippingLine1Target) {
      data.shipping_line1 = this.shippingLine1Target.value;
    }
    if (this.hasShippingLine2Target) {
      data.shipping_line2 = this.shippingLine2Target.value;
    }
    if (this.hasShippingPostcodeTarget) {
      data.shipping_postcode = this.shippingPostcodeTarget.value;
    }
    if (this.hasShippingCityTarget) {
      data.shipping_city = this.shippingCityTarget.value;
    }
    if (this.hasPhoneCountryTarget) {
      data.shipping_phone_country = this.phoneCountryTarget.value;
    }
    if (this.hasPhoneNumberTarget) {
      data.shipping_phone_number = this.phoneNumberTarget.value;
    }

    return data;
  }

  loadSavedData() {
    try {
      const savedData = localStorage.getItem(this.storageKey);
      if (!savedData) return;

      const data = JSON.parse(savedData);
      this.fillFormData(data);

      if (this.hasCheckboxTarget) {
        this.checkboxTarget.checked = true;
      }
    } catch (error) {
      console.error("Failed to load form data from localStorage:", error);
    }
  }

  fillFormData(data) {
    // Contact information
    if (this.hasEmailTarget && data.email) {
      this.emailTarget.value = data.email;
    }
    if (this.hasMarketingOptInTarget && data.marketing_opt_in !== undefined) {
      this.marketingOptInTarget.checked = data.marketing_opt_in;
    }

    // Shipping address
    if (this.hasShippingNameTarget && data.shipping_name) {
      this.shippingNameTarget.value = data.shipping_name;
    }
    if (this.hasShippingCompanyTarget && data.shipping_company) {
      this.shippingCompanyTarget.value = data.shipping_company;
    }
    if (this.hasShippingLine1Target && data.shipping_line1) {
      this.shippingLine1Target.value = data.shipping_line1;
    }
    if (this.hasShippingLine2Target && data.shipping_line2) {
      this.shippingLine2Target.value = data.shipping_line2;
    }
    if (this.hasShippingPostcodeTarget && data.shipping_postcode) {
      this.shippingPostcodeTarget.value = data.shipping_postcode;
    }
    if (this.hasShippingCityTarget && data.shipping_city) {
      this.shippingCityTarget.value = data.shipping_city;
    }
    if (this.hasPhoneCountryTarget && data.shipping_phone_country) {
      this.phoneCountryTarget.value = data.shipping_phone_country;
    }
    if (this.hasPhoneNumberTarget && data.shipping_phone_number) {
      this.phoneNumberTarget.value = data.shipping_phone_number;
    }
  }

  addFieldListeners() {
    const fieldTargets = [
      "email",
      "marketingOptIn",
      "shippingName",
      "shippingCompany",
      "shippingLine1",
      "shippingLine2",
      "shippingPostcode",
      "shippingCity",
      "phoneCountry",
      "phoneNumber",
    ];

    fieldTargets.forEach((targetName) => {
      const hasTarget = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`;
      if (!this[hasTarget]) return;

      const target = this[`${targetName.charAt(0).toLowerCase() + targetName.slice(1)}Target`];
      target.addEventListener("input", () => this.updateSavedData());
      target.addEventListener("change", () => this.updateSavedData());
    });
  }

  updateSavedData() {
    if (this.hasCheckboxTarget && this.checkboxTarget.checked) {
      this.saveFormData();
    }
  }
}
